import { useCallback, useRef, useState } from 'react';

export type FormErrors<T> = Partial<Record<keyof T, string>>;

export type UseFormConfig<T> = {
  initial: T;
  /** Synchronous per-field validation. Return an empty object for "all good". */
  validate?: (values: T) => FormErrors<T>;
  /** Called only after `validate` returns no errors. May be async. */
  onSubmit: (values: T) => Promise<void> | void;
};

export type UseFormResult<T> = {
  values: T;
  errors: FormErrors<T>;
  /** Top-level submit failure message (e.g. caught from onSubmit). */
  submitError: string | null;
  isPending: boolean;
  set<K extends keyof T>(name: K, value: T[K]): void;
  setAll(next: Partial<T>): void;
  /** Reset to the initial values + clear errors / submitError / isPending. */
  reset(): void;
  /** Validate, then call `onSubmit`. Catches and stores any thrown error. */
  submit(): Promise<void>;
};

/**
 * Lightweight controlled-form hook. Pairs with `<Form>` + `<FormField>` for
 * label + input + error display. Intentionally tiny — no field-level subscribe,
 * no schema integration, no register/unregister. If a form gets complex enough
 * to need those, lift it to react-hook-form at that page only.
 */
export function useForm<T>(config: UseFormConfig<T>): UseFormResult<T> {
  const initialRef = useRef(config.initial);
  // Keep `validate` / `onSubmit` in refs so callers can pass inline closures
  // without retriggering memoization further down.
  const validateRef = useRef(config.validate);
  validateRef.current = config.validate;
  const onSubmitRef = useRef(config.onSubmit);
  onSubmitRef.current = config.onSubmit;

  const [values, setValues] = useState<T>(initialRef.current);
  const [errors, setErrors] = useState<FormErrors<T>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  const set = useCallback(<K extends keyof T>(name: K, value: T[K]) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    // Clear field-level error eagerly — re-validated at submit.
    setErrors((prev) => {
      if (!(name in prev)) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const setAll = useCallback((next: Partial<T>) => {
    setValues((prev) => ({ ...prev, ...next }));
  }, []);

  const reset = useCallback(() => {
    setValues(initialRef.current);
    setErrors({});
    setSubmitError(null);
    setIsPending(false);
  }, []);

  const submit = useCallback(async () => {
    setSubmitError(null);
    const v = validateRef.current?.(values) ?? {};
    if (Object.keys(v).length > 0) {
      setErrors(v);
      return;
    }
    setErrors({});
    setIsPending(true);
    try {
      await onSubmitRef.current(values);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setIsPending(false);
    }
  }, [values]);

  return { values, errors, submitError, isPending, set, setAll, reset, submit };
}
